import { getJWTToken } from '../utils/auth';
import { authHeaders } from './impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type EffectiveCondition = { measure: string; minimum: number };

export type EffectiveRuleSet = {
  outcome: 'exits' | 'eligible_for_review';
  conditions: EffectiveCondition[];
  displayNote?: string;
};

export type EffectiveGradeBand = {
  assessment: 'access' | 'alternate_access';
  grades: string[] | 'all';
  noExit: boolean;
  ruleSets: EffectiveRuleSet[];
};

export type EffectiveExitCriteria = {
  source: 'state_record' | 'district';
  effectiveFrom: string;
  gradeBands: EffectiveGradeBand[];
};

export type DistrictExitCriteria = {
  state: string;
  stateName: string;
  effective: EffectiveExitCriteria | null;
};

function asCondition(value: unknown): EffectiveCondition {
  const c = value as Record<string, unknown> | null;
  if (!c || typeof c.measure !== 'string' || typeof c.minimum !== 'number') {
    throw new Error('exit-criteria response has a malformed condition');
  }
  return { measure: c.measure, minimum: c.minimum };
}

function asRuleSet(value: unknown): EffectiveRuleSet {
  const r = value as Record<string, unknown> | null;
  if (!r || (r.outcome !== 'exits' && r.outcome !== 'eligible_for_review') || !Array.isArray(r.conditions)) {
    throw new Error('exit-criteria response has a malformed rule set');
  }
  return {
    outcome: r.outcome,
    conditions: r.conditions.map(asCondition),
    ...(typeof r.displayNote === 'string' ? { displayNote: r.displayNote } : {}),
  };
}

function asGradeBand(value: unknown): EffectiveGradeBand {
  const b = value as Record<string, unknown> | null;
  if (!b || (b.assessment !== 'access' && b.assessment !== 'alternate_access') || !Array.isArray(b.ruleSets)) {
    throw new Error('exit-criteria response has a malformed grade band');
  }
  const grades = b.grades === 'all' || b.grades == null ? 'all' : b.grades;
  if (grades !== 'all' && !(Array.isArray(grades) && grades.every((g) => typeof g === 'string'))) {
    throw new Error('exit-criteria response has malformed grades');
  }
  return {
    assessment: b.assessment,
    grades: grades as string[] | 'all',
    noExit: b.noExit === true,
    ruleSets: b.ruleSets.map(asRuleSet),
  };
}

function asEffective(value: unknown): EffectiveExitCriteria | null {
  if (value == null) return null;
  const e = value as Record<string, unknown>;
  if ((e.source !== 'state_record' && e.source !== 'district') || typeof e.effectiveFrom !== 'string' || !Array.isArray(e.gradeBands)) {
    throw new Error('exit-criteria response has malformed effective criteria');
  }
  return { source: e.source, effectiveFrom: e.effectiveFrom, gradeBands: e.gradeBands.map(asGradeBand) };
}

function asDistrictExitCriteria(body: unknown): DistrictExitCriteria {
  const d = body as Record<string, unknown> | null;
  if (!d || typeof d.state !== 'string') {
    throw new Error('exit-criteria response names no state');
  }
  return {
    state: d.state,
    stateName: typeof d.stateName === 'string' && d.stateName !== '' ? d.stateName : d.state,
    effective: asEffective(d.effective),
  };
}

export async function fetchDistrictExitCriteria(): Promise<DistrictExitCriteria> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/settings/exit-criteria`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`fetch-exit-criteria failed: ${response.status}`);
  }
  return asDistrictExitCriteria(await response.json());
}
